import { oc } from "@orpc/contract";
import { z } from "zod";
import { CartVatRate, VatTreatment } from "./cart";
import { AddressCountry } from "./customer-address";
import { listInput, listPage } from "./list";
import { Cents } from "./money";
import { PaymentStatus as TransactionStatus } from "./payment";
import { VatBasis } from "./price";
import { CreditInvoiceSummary, Refund, RefundableOrder } from "./refund";

/**
 * Orders (CONTEXT.md `order`, `orderLine`, `orderEvent`): the schemas the shop's checkout answers
 * with, and the orders admin builds on. An order is a snapshot: its addresses, lines, VAT per line,
 * shipping, discount and totals are what they were when it was placed.
 *
 * The statuses are separate (CONTEXT.md):
 * - `paymentStatus`: `open` (waiting for the payment, stock reserved), `paid`, `on_account`,
 *   `partially_refunded`, `refunded`, `expired` (not paid in time: stock and code use given back);
 * - `shipmentStatus`: `to_ship`, `shipped`;
 * - `orderStatus`: `open`, `completed`, `cancelled`.
 */

export const PaymentStatus = z.enum([
	"open",
	"paid",
	"on_account",
	"partially_refunded",
	"refunded",
	"expired",
]);
export type PaymentStatus = z.infer<typeof PaymentStatus>;

export const ShipmentStatus = z.enum(["to_ship", "shipped"]);
export type ShipmentStatus = z.infer<typeof ShipmentStatus>;

export const OrderStatus = z.enum(["open", "completed", "cancelled"]);
export type OrderStatus = z.infer<typeof OrderStatus>;

/**
 * How the customer pays: through Pay.nl (iDEAL, Bancontact, card, PayPal or bank transfer), or on
 * account (`onAccount`, #97): only an approved business customer, who pays the invoice later by a
 * Pay.nl bank transfer.
 */
export const PaymentMethod = z.enum(["ideal", "bancontact", "card", "paypal", "bankTransfer", "onAccount"]);
export type PaymentMethod = z.infer<typeof PaymentMethod>;

/**
 * What happened to an order: placed, paid, expired, cancelled, shipped (the first tracking code),
 * the tracking code corrected, refunded (or a refund Pay.nl refused), a note, or a payment reminder
 * mailed for its on-account invoice.
 */
export const OrderEventKind = z.enum([
	"placed",
	"paid",
	"expired",
	"cancelled",
	"shipped",
	"trackingChanged",
	"refunded",
	"refundFailed",
	"note",
	"reminderSent",
	"shippingAddressChecked",
]);
export type OrderEventKind = z.infer<typeof OrderEventKind>;

/** An address as the order copied it. */
export const OrderAddress = z.object({
	recipientName: z.string(),
	company: z.string().nullable(),
	street: z.string(),
	houseNumber: z.string(),
	houseNumberAddition: z.string().nullable(),
	postcode: z.string(),
	city: z.string(),
	country: AddressCountry,
});
export type OrderAddress = z.infer<typeof OrderAddress>;

export const OrderLine = z.object({
	variantId: z.string(),
	productName: z.string(),
	sku: z.string(),
	/** The option values as sold: `Formaat: XXL`, joined with ", ". Empty without options. */
	options: z.string(),
	quantity: z.number().int(),
	/** Per unit, on the order's `priceDisplay`. */
	unitPrice: Cents,
	/** `unitPrice × quantity`. */
	lineTotal: Cents,
	/** The discount code's share of this line. */
	discount: Cents,
	/** The line after its discount, taxed; `vatRate` is what was charged (0 under reverse charge). */
	vat: CartVatRate,
});
export type OrderLine = z.infer<typeof OrderLine>;

export const OrderTotals = z.object({
	goods: Cents,
	/** What the code took off goods and shipping; `null` without a code. */
	discount: Cents.nullable(),
	/** The code, as the customer typed it. */
	discountCode: z.string().nullable(),
	/** The shipping cost before discount; `null` when the cart had none. */
	shipping: Cents.nullable(),
	/** The shipping line after its discount, taxed; `null` without shipping. */
	shippingVat: CartVatRate.nullable(),
	/** One row per VAT rate over the lines and shipping, highest first. */
	vatRates: z.array(CartVatRate),
	totalExcl: Cents,
	totalVat: Cents,
	totalIncl: Cents,
	toPay: Cents,
});
export type OrderTotals = z.infer<typeof OrderTotals>;

/** An order as its customer sees it. */
export const ShopOrder = z.object({
	id: z.string(),
	/** `CBO-` and a rising number from 100000: what the customer quotes. */
	orderNumber: z.string(),
	placedAt: z.iso.datetime(),
	/**
	 * Until when the stock is held for an unpaid order (30 minutes, 7 days for a bank transfer);
	 * `null` once paid.
	 */
	expiresAt: z.iso.datetime().nullable(),
	/** On account: the last day to pay the invoice (`YYYY-MM-DD`); `null` for any other method. */
	dueOn: z.string().nullable(),
	paymentMethod: PaymentMethod,
	paymentStatus: PaymentStatus,
	shipmentStatus: ShipmentStatus,
	orderStatus: OrderStatus,
	email: z.string(),
	firstName: z.string(),
	lastName: z.string(),
	phone: z.string().nullable(),
	shippingAddress: OrderAddress,
	billingAddress: OrderAddress,
	vatTreatment: VatTreatment,
	reverseCharge: z.boolean(),
	priceDisplay: VatBasis,
	lines: z.array(OrderLine),
	totals: OrderTotals,
});
export type ShopOrder = z.infer<typeof ShopOrder>;

// ---------------------------------------------------------------------------------------------
// The orders admin (#99-#102): staff only, under `/admin/orders`.

/** Who ships the parcel (spec, "Orders, facturen en mail"): the track-and-trace mail links to its site. */
export const Carrier = z.enum(["postnl", "dhl", "dpd", "bpost"]);
export type Carrier = z.infer<typeof Carrier>;

export const OrderListItem = z.object({
	id: z.string(),
	orderNumber: z.string(),
	placedAt: z.iso.datetime(),
	/** The contact's first and last name. */
	name: z.string(),
	email: z.string(),
	/** The billing address's company, else the shipping address's; `null` for neither. */
	company: z.string().nullable(),
	totalIncl: Cents,
	paymentMethod: PaymentMethod,
	paymentStatus: PaymentStatus,
	shipmentStatus: ShipmentStatus,
	orderStatus: OrderStatus,
});
export type OrderListItem = z.infer<typeof OrderListItem>;

export const OrderListInput = listInput({
	sort: ["placedAt", "orderNumber", "name", "total"],
	defaultSort: "placedAt",
	defaultOrder: "desc",
	filters: {
		paymentStatus: PaymentStatus.optional().catch(undefined),
		shipmentStatus: ShipmentStatus.optional().catch(undefined),
		orderStatus: OrderStatus.optional().catch(undefined),
		/** Only orders to ship: paid for (paid, on account, partly refunded), not cancelled, not shipped. */
		toShip: z.enum(["yes"]).optional().catch(undefined),
	},
});
export type OrderListInput = z.input<typeof OrderListInput>;
/** The list query with its defaults filled in, as the API receives it. */
export type OrderListQuery = z.output<typeof OrderListInput>;

export const OrderList = listPage(OrderListItem);
export type OrderList = z.infer<typeof OrderList>;

/** A Pay.nl transaction for the order, read-only (the payment area writes them). */
export const OrderPayment = z.object({
	id: z.string(),
	method: PaymentMethod,
	/** Where the transaction stands at Pay.nl (the payment area's `PaymentStatus`). */
	status: TransactionStatus,
	amount: Cents,
	/** Pay.nl's transaction id; `null` until Pay.nl answered. */
	paynlOrderId: z.string().nullable(),
	testMode: z.boolean(),
	createdAt: z.iso.datetime(),
	paidAt: z.iso.datetime().nullable(),
});
export type OrderPayment = z.infer<typeof OrderPayment>;

/**
 * What an employee must do about an order event, for the start page's work queue (spec, "Te late
 * betaling"): `checkStock` (paid after the order expired, or stock short: check stock before
 * shipping), `refund` (paid on a cancelled order, or paid twice), `checkPayment` (a payment that
 * could not be booked, such as another amount than the order's), `createInvoice` (paid, but the
 * invoice could not be made: reverse charge without the customer's VAT id; resolved once an
 * employee makes it), `checkShippingAddress` (an order on account to a shipping address that is not
 * in the customer's address book, #222: it does not ship until an employee resolves it, "Adres
 * gecontroleerd").
 */
export const OrderEventAttention = z.enum([
	"checkStock",
	"refund",
	"checkPayment",
	"createInvoice",
	"checkShippingAddress",
]);
export type OrderEventAttention = z.infer<typeof OrderEventAttention>;

/** One line of the order's history: an order event, or a mail about the order. */
export const OrderHistoryEntry = z.discriminatedUnion("type", [
	z.object({
		type: z.literal("event"),
		id: z.string(),
		kind: OrderEventKind,
		message: z.string().nullable(),
		/** The employee who did it; `null` for the customer or the system. */
		employee: z.object({ id: z.string(), name: z.string() }).nullable(),
		at: z.iso.datetime(),
		/** What an employee must do about it; `null` for nothing. */
		attention: OrderEventAttention.nullable(),
		/** When and by whom the attention was marked handled; `null` while open (or without attention). */
		resolved: z
			.object({ at: z.iso.datetime(), employee: z.object({ id: z.string(), name: z.string() }).nullable() })
			.nullable(),
	}),
	z.object({
		type: z.literal("mail"),
		id: z.string(),
		/** The mail template (`trackAndTrace`, ...). */
		template: z.string(),
		subject: z.string(),
		recipient: z.string(),
		status: z.enum(["pending", "sent", "simulated", "failed"]),
		at: z.iso.datetime(),
	}),
]);
export type OrderHistoryEntry = z.infer<typeof OrderHistoryEntry>;

/** An order as the admin sees it: the customer's view, plus who, how it ships and its history. */
export const AdminOrder = ShopOrder.extend({
	customerId: z.string(),
	paidAt: z.iso.datetime().nullable(),
	carrier: Carrier.nullable(),
	trackingCode: z.string().nullable(),
	/** The carrier's tracking page for the code; `null` without one. */
	trackingUrl: z.string().nullable(),
	/** The Pay.nl transactions, newest first. */
	payments: z.array(OrderPayment),
	/** Events and mails, newest first. */
	history: z.array(OrderHistoryEntry),
	/** Its refunds (#103), oldest first, each with its credit invoice (#104). */
	refunds: z.array(Refund),
	/** What can still be refunded; `null` when nothing can (unpaid, on account, refunded in full). */
	refundable: RefundableOrder.nullable(),
	/** An order on account cancelled before it was paid: the credit invoice for its invoice. */
	cancellationCredit: CreditInvoiceSummary.nullable(),
	/**
	 * An employee can cancel it directly: open and unpaid, or on account and not shipped (with a
	 * credit invoice). A paid order is cancelled by refunding it.
	 */
	cancellable: z.boolean(),
	/**
	 * Paid (or on account), not cancelled, and no shipping address waiting to be checked: a
	 * tracking code can be entered.
	 */
	shippable: z.boolean(),
	/**
	 * An order on account to a shipping address not in the address book (#222) waits with shipping
	 * until an employee checks it ("Adres gecontroleerd": `resolveAttention` with this event). Null
	 * when nothing waits.
	 */
	addressCheck: z.object({ eventId: z.string() }).nullable(),
});
export type AdminOrder = z.infer<typeof AdminOrder>;

export const OrderPath = z.object({ orderId: z.string() });

export const OrderEventPath = OrderPath.extend({ eventId: z.string() });

export const AddOrderNoteBody = z.object({ message: z.string().trim().min(1).max(2000) });
export type AddOrderNoteBody = z.infer<typeof AddOrderNoteBody>;

/**
 * Why the order is cancelled, for the history; optional, and an empty reason counts as none.
 * Without a reason the client sends no body (the input is then only the path); the API takes that.
 */
export const CancelOrderBody = z.object({ reason: z.string().trim().max(500).optional() });
export type CancelOrderBody = z.infer<typeof CancelOrderBody>;

export const SetTrackingBody = z.object({
	carrier: Carrier,
	/** As printed on the label; spaces are left out and letters are stored in capitals. */
	trackingCode: z
		.string()
		.trim()
		.min(1)
		.max(64)
		.regex(/^[A-Za-z0-9 -]+$/),
	/**
	 * Mail the customer the tracking link. The first code always mails; on a correction (or the same
	 * code again) only when this is true.
	 */
	mailCustomer: z.boolean().default(false),
});
export type SetTrackingBody = z.input<typeof SetTrackingBody>;

/** The orders admin, for signed-in employees only. */
export const OrderContract = {
	/**
	 * A page of orders, newest first by default: search on order number, name, email and company;
	 * filter on payment, shipment and order status.
	 */
	list: oc.route({ method: "GET", path: "/admin/orders" }).input(OrderListInput).output(OrderList),
	/** One order with its payments and history. Errors: `OrderNotFound` (404). */
	get: oc.route({ method: "GET", path: "/admin/orders/{orderId}" }).input(OrderPath).output(AdminOrder),
	/** Adds a note to the order's history, signed with the employee. Errors: `OrderNotFound` (404). */
	addNote: oc
		.route({ method: "POST", path: "/admin/orders/{orderId}/notes" })
		.input(OrderPath.extend(AddOrderNoteBody.shape))
		.output(AdminOrder),
	/**
	 * Cancels an open, unpaid order: its reservations and discount code use are given back. The
	 * optional reason goes in the `cancelled` event. Errors: `OrderNotFound` (404),
	 * `OrderNotCancellable` (409: paid; refund it instead).
	 */
	cancel: oc
		.route({ method: "POST", path: "/admin/orders/{orderId}/cancel" })
		.input(OrderPath.extend(CancelOrderBody.shape))
		.output(AdminOrder),
	/**
	 * Sets the carrier and tracking code: the order is shipped (and completed once paid), the
	 * customer gets the track-and-trace mail. A corrected code is logged, and mailed only with
	 * `mailCustomer`. Errors: `OrderNotFound` (404), `OrderNotShippable` (409: unpaid or cancelled).
	 */
	setTracking: oc
		.route({ method: "POST", path: "/admin/orders/{orderId}/tracking" })
		.input(OrderPath.extend(SetTrackingBody.shape))
		.output(AdminOrder),
	/**
	 * Marks an event's attention handled ("Afgehandeld"): it leaves the start page's work queue.
	 * Marking it again changes nothing. Errors: `OrderEventNotFound` (404: not this order's, or
	 * nothing to handle).
	 */
	resolveAttention: oc
		.route({ method: "POST", path: "/admin/orders/{orderId}/events/{eventId}/resolve" })
		.input(OrderEventPath)
		.output(AdminOrder),
};
